import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchQueue, type QueueLine, type QueueView } from "../../services/http.js";
import { TrackPanel } from "./TrackPanel.js";

/**
 * What needs a human, most urgent first.
 *
 * # The number beside a track is *parties*, not votes
 *
 * Five `off` votes from one room is one opinion; two from two rooms is the
 * threshold that took the card out of the deal. Showing the vote count alone
 * would make a table that did not like a song look like a consensus, which is
 * the judgement error the whole suspension rule was designed to avoid — so both
 * numbers are on screen and the parties one is the one in words.
 *
 * # A failed load reads as "nothing here"
 *
 * The server answers 404 to anybody who is not a curator, and this must not
 * translate that into "forbidden" — see [`AdminPage`](./AdminPage.js).
 */
export function QueuePanel() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<QueueView | null>(null);
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    void fetchQueue().then(
      (loaded) => {
        setQueue(loaded);
        setMissing(false);
      },
      () => {
        setMissing(true);
      },
    );
  }, []);

  useEffect(load, [load]);

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (!queue) {
    return <p className="text-sm text-white/50">{t("admin.queue.reading")}</p>;
  }
  if (queue.tracks.length === 0) {
    return <p className="text-sm text-white/60">{t("admin.queue.empty")}</p>;
  }

  return (
    <section aria-label={t("admin.queue.label")} className="flex flex-col gap-3">
      <p className="text-xs text-white/40">
        {t("admin.queue.summary", {
          waiting: queue.waiting,
          parties: queue.parties_to_suspend,
        })}
      </p>

      {queue.tracks.map((line) =>
        open === line.track_id ? (
          <TrackPanel
            key={line.track_id}
            trackId={line.track_id}
            onClose={() => {
              setOpen(null);
            }}
            onDecided={() => {
              setOpen(null);
              load();
            }}
          />
        ) : (
          <Row
            key={line.track_id}
            line={line}
            onOpen={() => {
              setOpen(line.track_id);
            }}
          />
        ),
      )}
    </section>
  );
}

function Row({ line, onOpen }: { line: QueueLine; onOpen: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-baseline justify-between gap-3 rounded-lg border border-white/15 bg-stage-900 px-3 py-3 text-left"
    >
      <span className="min-w-0">
        <span className="block truncate font-medium">
          {line.title}
          <span className="text-white/50"> — {line.artist_credit}</span>
        </span>
        <span className="block text-xs text-white/50">
          {line.year ?? t("admin.queue.noYear")} · {line.confidence}
          {line.suspended && (
            <span className="text-coin-400"> · {t("admin.queue.suspended")}</span>
          )}
        </span>
      </span>
      <span className="shrink-0 text-right text-xs tabular-nums text-white/60">
        {/* Parties in words, votes as the raw count. The first is the number
            the rule uses; the second is how loud the room was. */}
        <span className="block">
          {t("admin.queue.partiesSayOff", { count: line.parties_against })}
        </span>
        <span className="block text-white/40">
          {t("admin.queue.votes", { off: line.votes_off, keep: line.votes_keep })}
          {line.open_reports > 0 &&
            ` · ${t("admin.queue.reports", { count: line.open_reports })}`}
        </span>
      </span>
    </button>
  );
}
